import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available cities (sync with location filter, no Others option)
const CITIES = [
  'Bengaluru',
  'Delhi',
  'Gurgaon',
  'Mumbai',
  'Hyderabad',
  'Pune',
  'Chennai',
  'Kolkata'
];

export default function ListEventDialog({ open, onOpenChange }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    l1_category: '',
    l2_category: '',
    icon: '',
    location: '',
    city: 'Bangalore',
    eventDate: '',
    startTime: '',
    endTime: '',
    type: 'open',
    price: '',
    requiresRegistration: 'false',
    registrationUrl: '',
    submitterName: '',
    submitterEmail: '',
    submitterPhone: ''
  });

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/listing-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Event listing submitted! We\'ll review it shortly.');
        resetForm();
        onOpenChange(false);
      } else {
        toast.error('Failed to submit event listing');
      }
    } catch (error) {
      console.error('Error submitting listing:', error);
      toast.error('An error occurred. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      l1_category: '',
      l2_category: '',
      icon: '',
      location: '',
      city: 'Bangalore',
      eventDate: '',
      startTime: '',
      endTime: '',
      type: 'open',
      price: '',
      requiresRegistration: 'false',
      registrationUrl: '',
      submitterName: '',
      submitterEmail: '',
      submitterPhone: ''
    });
    setSelectedDate(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>List Your Event</DialogTitle>
          <p className="text-sm text-muted-foreground">Share your event with the Tandem community</p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Event Details */}
            <div className="col-span-2">
              <Label htmlFor="title">Event Title *</Label>
              <Input 
                id="title" 
                required 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Weekend Yoga Session"
              />
            </div>
            
            <div className="col-span-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea 
                id="description" 
                required 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                placeholder="Tell us about your event..."
              />
            </div>

            <div>
              <Label htmlFor="l1_category">Category *</Label>
              <Select 
                required 
                value={formData.l1_category}
                onValueChange={(value) => {
                  setFormData({...formData, l1_category: value, l2_category: '', category: value});
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat._id} value={cat._id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="l2_category">Subcategory *</Label>
              <Select 
                required 
                value={formData.l2_category}
                onValueChange={(value) => {
                  const selectedCat = categories.find(c => c._id === formData.l1_category);
                  const selectedL2 = selectedCat?.l2_categories?.find(l2 => l2.id === value);
                  setFormData({
                    ...formData, 
                    l2_category: value,
                    icon: selectedL2?.emoji || formData.icon
                  });
                }}
                disabled={!formData.l1_category}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select subcategory" />
                </SelectTrigger>
                <SelectContent>
                  {categories
                    .find(c => c._id === formData.l1_category)
                    ?.l2_categories?.map((l2) => (
                      <SelectItem key={l2.id} value={l2.id}>
                        {l2.emoji} {l2.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="type">Event Type *</Label>
              <Select 
                required 
                value={formData.type}
                onValueChange={(value) => setFormData({...formData, type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="invite-only">Invite Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="location">Location *</Label>
              <Input 
                id="location" 
                required 
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="e.g., Koramangala"
              />
            </div>

            <div>
              <Label htmlFor="city">City *</Label>
              <Select 
                required 
                value={formData.city}
                onValueChange={(value) => setFormData({...formData, city: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  {CITIES.map((city) => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label>Event Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setFormData({...formData, eventDate: format(date, 'yyyy-MM-dd')});
                    }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="startTime">Start Time *</Label>
              <Input 
                id="startTime" 
                type="time" 
                required 
                value={formData.startTime}
                onChange={(e) => setFormData({...formData, startTime: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="endTime">End Time *</Label>
              <Input 
                id="endTime" 
                type="time" 
                required 
                value={formData.endTime}
                onChange={(e) => setFormData({...formData, endTime: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="price">Price *</Label>
              <Input 
                id="price" 
                required 
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                placeholder="Free or ₹500" 
              />
            </div>

            <div>
              <Label htmlFor="requiresRegistration">Registration Required? *</Label>
              <Select 
                required 
                value={formData.requiresRegistration}
                onValueChange={(value) => setFormData({...formData, requiresRegistration: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label htmlFor="registrationUrl">Registration URL</Label>
              <Input 
                id="registrationUrl" 
                type="url" 
                value={formData.registrationUrl}
                onChange={(e) => setFormData({...formData, registrationUrl: e.target.value})}
                placeholder="https://example.com (if applicable)" 
              />
            </div>

            {/* Submitter Details */}
            <div className="col-span-2 pt-4 border-t border-border">
              <h3 className="text-sm font-semibold text-foreground mb-3">Your Contact Information</h3>
            </div>

            <div className="col-span-2">
              <Label htmlFor="submitterName">Your Name *</Label>
              <Input 
                id="submitterName" 
                required 
                value={formData.submitterName}
                onChange={(e) => setFormData({...formData, submitterName: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="submitterEmail">Your Email *</Label>
              <Input 
                id="submitterEmail" 
                type="email" 
                required 
                value={formData.submitterEmail}
                onChange={(e) => setFormData({...formData, submitterEmail: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="submitterPhone">Your Phone</Label>
              <Input 
                id="submitterPhone" 
                type="tel" 
                value={formData.submitterPhone}
                onChange={(e) => setFormData({...formData, submitterPhone: e.target.value})}
                placeholder="+91XXXXXXXXXX"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="gradient-primary text-primary-foreground">
              Submit Event
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
